import React, { useState, useRef, useCallback } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  Label
} from 'recharts';
import {
  Box, 
  Text, 
  HStack
} from '@chakra-ui/react';

// This component uses the exact styling from the reference image with enhanced features
const NeonDashboardChart = ({ chartVisibleSeries = {}, timeframe = "24h" }) => {
  const chartRef = useRef(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0, active: false });
  const [tooltipData, setTooltipData] = useState(null);
  
  // Generate chart data - Full dataset (all)
  const allData = [
    { 
      date: 'Feb 1', 
      jinuPrice: 0.0000, 
      solPrice: 160.00, 
      userProfitLoss: 0.0000, 
      progressiveJackpot: 10000,
      activePlayers: 100,
      activeBorrowers: 0.0000, 
      activeStakers: 200,
      activeLenders: 50
    },
    { 
      date: 'Feb 15', 
      jinuPrice: 0.0005, 
      solPrice: 158.00, 
      userProfitLoss: 0.0005, 
      progressiveJackpot: 15000,
      activePlayers: 250,
      activeBorrowers: 0.0008, 
      activeStakers: 300,
      activeLenders: 75
    },
    { 
      date: 'Mar 1', 
      jinuPrice: 0.0010, 
      solPrice: 155.00, 
      userProfitLoss: 0.0010, 
      progressiveJackpot: 25000,
      activePlayers: 400,
      activeBorrowers: 0.0015, 
      activeStakers: 400,
      activeLenders: 100
    },
    { 
      date: 'Mar 10', 
      jinuPrice: 0.0015, 
      solPrice: 150.00, 
      userProfitLoss: 0.0015, 
      progressiveJackpot: 35000,
      activePlayers: 600,
      activeBorrowers: 0.0020, 
      activeStakers: 500,
      activeLenders: 125
    },
    { 
      date: 'Mar 20', 
      jinuPrice: 0.0020, 
      solPrice: 147.00, 
      userProfitLoss: 0.0018, 
      progressiveJackpot: 40000,
      activePlayers: 800,
      activeBorrowers: 0.0022, 
      activeStakers: 650,
      activeLenders: 150
    },
    { 
      date: 'Mar 25', 
      jinuPrice: 0.0040, 
      solPrice: 146.50, 
      userProfitLoss: 0.0020, 
      progressiveJackpot: 45000,
      activePlayers: 1250,
      activeBorrowers: 0.0025, 
      activeStakers: 750,
      activeLenders: 175
    },
    { 
      date: 'Mar 26', 
      jinuPrice: 0.0050, 
      solPrice: 142.75, 
      userProfitLoss: 0.0030, 
      progressiveJackpot: 47500,
      activePlayers: 1350,
      activeBorrowers: 0.0035, 
      activeStakers: 780,
      activeLenders: 200
    },
    { 
      date: 'Mar 27', 
      jinuPrice: 0.0065, 
      solPrice: 139.20, 
      userProfitLoss: 0.0050, 
      progressiveJackpot: 50000,
      activePlayers: 1450,
      activeBorrowers: 0.0045, 
      activeStakers: 810,
      activeLenders: 225
    },
    { 
      date: 'Mar 28', 
      jinuPrice: 0.0080, 
      solPrice: 136.50, 
      userProfitLoss: 0.0065, 
      progressiveJackpot: 52500,
      activePlayers: 1550,
      activeBorrowers: 0.0055, 
      activeStakers: 840,
      activeLenders: 250
    },
    { 
      date: 'Mar 29', 
      jinuPrice: 0.0095, 
      solPrice: 133.75, 
      userProfitLoss: 0.0080, 
      progressiveJackpot: 55000,
      activePlayers: 1650,
      activeBorrowers: 0.0065, 
      activeStakers: 870,
      activeLenders: 275
    },
    { 
      date: 'Mar 30', 
      jinuPrice: 0.0115, 
      solPrice: 130.25, 
      userProfitLoss: 0.0095, 
      progressiveJackpot: 57500,
      activePlayers: 1750,
      activeBorrowers: 0.0085, 
      activeStakers: 900,
      activeLenders: 300
    },
    { 
      date: 'Mar 31', 
      jinuPrice: 0.0125, 
      solPrice: 127.80, 
      userProfitLoss: 0.0110, 
      progressiveJackpot: 60000,
      activePlayers: 1850,
      activeBorrowers: 0.0105, 
      activeStakers: 930,
      activeLenders: 325
    },
    { 
      date: 'Apr 1', 
      jinuPrice: 0.0136, 
      solPrice: 125.00, 
      userProfitLoss: 0.0125, 
      progressiveJackpot: 62500,
      activePlayers: 1950,
      activeBorrowers: 0.0130, 
      activeStakers: 960,
      activeLenders: 350
    },
  ];

  // Get data based on selected timeframe
  const getTimeframeData = () => {
    switch(timeframe) {
      case "24h":
        return allData.slice(-2); // Last 2 days
      case "7d":
        return allData.slice(-7); // Last 7 days
      case "30d":
        return allData.slice(-10); // Last 10 days (simulating 30 days)
      case "all":
      default:
        return allData;
    }
  };

  const data = getTimeframeData();

  // Format numbers in a compact way (e.g., 1000 -> 1k)
  const formatCompact = (value) => {
    if (value >= 1000000) {
      return (value / 1000000).toFixed(1) + 'M';
    } else if (value >= 1000) {
      return (value / 1000).toFixed(1) + 'k';
    } else if (value >= 1) {
      return value.toFixed(1);
    } else {
      // For small decimal values
      return value.toFixed(3);
    }
  };

  // Handle mouse move on chart
  const handleMouseMove = useCallback((e) => {
    if (e && e.activeTooltipIndex !== undefined) {
      const chartData = data[e.activeTooltipIndex];
      setTooltipData({
        date: chartData.date,
        values: e.activePayload
      });
      setTooltipPos({
        x: e.chartX,
        y: e.chartY,
        active: true
      });
    } else {
      setTooltipPos({ ...tooltipPos, active: false });
    }
  }, [data, tooltipPos]);

  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Box 
          bg="rgba(0, 0, 0, 0.8)" 
          p={3} 
          borderRadius="md" 
          borderWidth="1px" 
          borderColor="rgba(0, 255, 170, 0.3)"
          boxShadow="0 0 10px rgba(0, 255, 170, 0.2)"
          minWidth="180px"
        >
          <Text color="#00FFAA" fontWeight="bold" mb={2}>{label}</Text>
          {payload.map((entry, index) => (
            <Text key={index} color={entry.color} fontSize="sm">
              {entry.name}: {
                entry.name === 'JINU Price' ? `$${entry.value.toFixed(6)}` :
                entry.name === 'SOL Price' ? `$${entry.value.toFixed(2)}` :
                entry.name === 'Your P/L' ? `$${entry.value.toFixed(4)}` :
                entry.name === 'Jackpot' ? `$${entry.value.toFixed(2)}` :
                entry.name === 'Active Borrowers' ? `$${entry.value.toFixed(4)}` :
                entry.name === 'Active Lenders' ? `$${entry.value.toFixed(0)}` :
                entry.value
              }
            </Text>
          ))}
        </Box>
      );
    }
    return null;
  };

  return (
    <>
      {/* Chart Container */}
      <Box 
        position="relative" 
        height="350px" 
        width="100%" 
        mt={2}
        p={4}
        bg="black"
        borderRadius="md"
        overflow="hidden"
        boxShadow="0 4px 12px rgba(0, 0, 0, 0.5)"
        borderWidth="1px"
        borderColor="rgba(0, 255, 170, 0.2)"
      >
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            ref={chartRef}
            data={data}
            margin={{
              top: 20,
              right: 50,
              left: 50,
              bottom: 20
            }}
            onMouseMove={handleMouseMove}
            onMouseLeave={() => setTooltipPos({ ...tooltipPos, active: false })}
          >
            <defs>
              <filter id="glow-cyan" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="2" result="blur" />
                <feComposite in="SourceGraphic" in2="blur" operator="over" />
              </filter>
              <filter id="glow-green" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="2" result="blur" />
                <feComposite in="SourceGraphic" in2="blur" operator="over" />
              </filter>
              <filter id="glow-magenta" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="2" result="blur" />
                <feComposite in="SourceGraphic" in2="blur" operator="over" />
              </filter>
              <filter id="glow-yellow" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="2" result="blur" />
                <feComposite in="SourceGraphic" in2="blur" operator="over" />
              </filter>
              <filter id="glow-purple" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="2" result="blur" />
                <feComposite in="SourceGraphic" in2="blur" operator="over" />
              </filter>
              <filter id="glow-orange" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="2" result="blur" />
                <feComposite in="SourceGraphic" in2="blur" operator="over" />
              </filter>
            </defs>
            
            <CartesianGrid 
              strokeDasharray="1 0" 
              stroke="rgba(255, 255, 255, 0.15)" 
              horizontal={true}
              vertical={true}
            />
            <XAxis 
              dataKey="date" 
              stroke="rgba(255, 255, 255, 0.7)" 
              tick={{ fill: "rgba(255, 255, 255, 0.7)" }}
              axisLine={{ stroke: "rgba(255, 255, 255, 0.3)" }}
              tickLine={{ stroke: "rgba(255, 255, 255, 0.3)" }}
            />
            
            {/* JINU Price Y-Axis (Left) */}
            <YAxis 
              yAxisId="jinuPrice" 
              orientation="left"
              stroke="#00E5FF"
              domain={[0, 'auto']}
              tick={false}
              axisLine={{ stroke: "rgba(0, 229, 255, 0.3)" }}
              tickLine={false}
            />
            
            {/* SOL Price Y-Axis (Right) */}
            <YAxis 
              yAxisId="solPrice" 
              orientation="right"
              stroke="#00FF85"
              domain={[100, 2000]}
              tick={false}
              axisLine={{ stroke: "rgba(0, 255, 133, 0.3)" }}
              tickLine={false}
            />

            {/* Jackpot Y-Axis (Far Right) - Hidden but used for scaling */}
            <YAxis 
              yAxisId="jackpot" 
              orientation="right"
              stroke="transparent"
              domain={[10000, 70000]}
              hide={true}
            />
            
            {/* Reference Line for Today */}
            <ReferenceLine x="Apr 1" yAxisId="jinuPrice" stroke="rgba(255, 255, 255, 0.5)" strokeDasharray="3 3" />
            
            <Tooltip content={<CustomTooltip />} />
            
            {/* JINU Price Line */}
            {chartVisibleSeries.jinuPrice && (
              <Line
                yAxisId="jinuPrice"
                type="monotone"
                dataKey="jinuPrice"
                stroke="#00E5FF"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, stroke: "#00E5FF", strokeWidth: 1, fill: "#00E5FF" }}
                isAnimationActive={true}
                animationDuration={1000}
                label={({ x, y, value, index }) => {
                  // Only show labels for the first and last points
                  if (index === 0 || index === data.length - 1) {
                    return (
                      <text 
                        x={index === 0 ? x - 40 : x + 40} 
                        y={y} 
                        fill="#00E5FF" 
                        fontSize={10} 
                        textAnchor={index === 0 ? "start" : "end"}
                      >
                        {formatCompact(value)}
                      </text>
                    );
                  }
                  return null;
                }}
              />
            )}
            
            {/* SOL Price Line */}
            {chartVisibleSeries.solPrice && (
              <Line
                yAxisId="solPrice"
                type="monotone"
                dataKey="solPrice"
                stroke="#00FF85"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, stroke: "#00FF85", strokeWidth: 1, fill: "#00FF85" }}
                isAnimationActive={true}
                animationDuration={1000}
                label={({ x, y, value, index }) => {
                  // Only show labels for the first and last points
                  if (index === 0 || index === data.length - 1) {
                    return (
                      <text 
                        x={index === 0 ? x - 40 : x + 40} 
                        y={y} 
                        fill="#00FF85" 
                        fontSize={10} 
                        textAnchor={index === 0 ? "start" : "end"}
                      >
                        {formatCompact(value)}
                      </text>
                    );
                  }
                  return null;
                }}
              />
            )}
            
            {/* User Profit/Loss Line */}
            {chartVisibleSeries.userProfitLoss && (
              <Line
                yAxisId="jinuPrice"
                type="monotone"
                dataKey="userProfitLoss"
                stroke="#FF00E5"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, stroke: "#FF00E5", strokeWidth: 1, fill: "#FF00E5" }}
                isAnimationActive={true}
                animationDuration={1000}
                label={({ x, y, value, index }) => {
                  // Only show labels for the first and last points
                  if (index === 0 || index === data.length - 1) {
                    return (
                      <text 
                        x={index === 0 ? x - 40 : x + 40} 
                        y={y} 
                        fill="#FF00E5" 
                        fontSize={10} 
                        textAnchor={index === 0 ? "start" : "end"}
                      >
                        {formatCompact(value)}
                      </text>
                    );
                  }
                  return null;
                }}
              />
            )}
            
            {/* Progressive Jackpot Line */}
            {chartVisibleSeries.progressiveJackpot && (
              <Line
                yAxisId="jackpot"
                type="monotone"
                dataKey="progressiveJackpot"
                stroke="#E5FF00"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, stroke: "#E5FF00", strokeWidth: 1, fill: "#E5FF00" }}
                isAnimationActive={true}
                animationDuration={1000}
                label={({ x, y, value, index }) => {
                  // Only show labels for the first and last points
                  if (index === 0 || index === data.length - 1) {
                    return (
                      <text 
                        x={index === 0 ? x - 40 : x + 40} 
                        y={y} 
                        fill="#E5FF00" 
                        fontSize={10} 
                        textAnchor={index === 0 ? "start" : "end"}
                      >
                        {formatCompact(value)}
                      </text>
                    );
                  }
                  return null;
                }}
              />
            )}
            
            {/* Active Players Line */}
            {chartVisibleSeries.activePlayers && (
              <Line
                yAxisId="solPrice"
                type="monotone"
                dataKey="activePlayers"
                stroke="#A78BFA"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, stroke: "#A78BFA", strokeWidth: 1, fill: "#A78BFA" }}
                isAnimationActive={true}
                animationDuration={1000}
                label={({ x, y, value, index }) => {
                  // Only show labels for the first and last points
                  if (index === 0 || index === data.length - 1) {
                    return (
                      <text 
                        x={index === 0 ? x - 40 : x + 40} 
                        y={y} 
                        fill="#A78BFA" 
                        fontSize={10} 
                        textAnchor={index === 0 ? "start" : "end"}
                      >
                        {formatCompact(value)}
                      </text>
                    );
                  }
                  return null;
                }}
              />
            )}
            
            {/* Active Borrowers Line */}
            {chartVisibleSeries.activeBorrowers && (
              <Line
                yAxisId="jinuPrice"
                type="monotone"
                dataKey="activeBorrowers"
                stroke="#FFA500"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, stroke: "#FFA500", strokeWidth: 1, fill: "#FFA500" }}
                isAnimationActive={true}
                animationDuration={1000}
                label={({ x, y, value, index }) => {
                  // Only show labels for the first and last points
                  if (index === 0 || index === data.length - 1) {
                    return (
                      <text 
                        x={index === 0 ? x - 40 : x + 40} 
                        y={y} 
                        fill="#FFA500" 
                        fontSize={10} 
                        textAnchor={index === 0 ? "start" : "end"}
                      >
                        {formatCompact(value)}
                      </text>
                    );
                  }
                  return null;
                }}
              />
            )}
            
            {/* Active Stakers Line */}
            {chartVisibleSeries.activeStakers && (
              <Line
                yAxisId="solPrice"
                type="monotone"
                dataKey="activeStakers"
                stroke="#00D1FF"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, stroke: "#00D1FF", strokeWidth: 1, fill: "#00D1FF" }}
                isAnimationActive={true}
                animationDuration={1000}
                label={({ x, y, value, index }) => {
                  // Only show labels for the first and last points
                  if (index === 0 || index === data.length - 1) {
                    return (
                      <text 
                        x={index === 0 ? x - 40 : x + 40} 
                        y={y} 
                        fill="#00D1FF" 
                        fontSize={10} 
                        textAnchor={index === 0 ? "start" : "end"}
                      >
                        {formatCompact(value)}
                      </text>
                    );
                  }
                  return null;
                }}
              />
            )}
            
            {/* Active Lenders Line */}
            {chartVisibleSeries.activeLenders && (
              <Line
                yAxisId="solPrice"
                type="monotone"
                dataKey="activeLenders"
                stroke="#FF5733"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, stroke: "#FF5733", strokeWidth: 1, fill: "#FF5733" }}
                isAnimationActive={true}
                animationDuration={1000}
                label={({ x, y, value, index }) => {
                  // Only show labels for the first and last points
                  if (index === 0 || index === data.length - 1) {
                    return (
                      <text 
                        x={index === 0 ? x - 40 : x + 40} 
                        y={y} 
                        fill="#FF5733" 
                        fontSize={10} 
                        textAnchor={index === 0 ? "start" : "end"}
                      >
                        {formatCompact(value)}
                      </text>
                    );
                  }
                  return null;
                }}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
        
        {/* SVG Filters for Glow Effects */}
        <Box as="svg" width="0" height="0">
          <defs>
            <filter id="glow-cyan" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="0.5" result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
            <filter id="glow-green" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="0.5" result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
            <filter id="glow-magenta" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="0.5" result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
            <filter id="glow-yellow" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="0.5" result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
            <filter id="glow-purple" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="0.5" result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
            <filter id="glow-orange" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="0.5" result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
          </defs>
        </Box>
      </Box>
    </>
  );
};

export default NeonDashboardChart;
